import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { showToastMessage } from 'enable-ui';
import { HttpStatusCode } from 'enums/http';
import { SystemEnum } from 'enums/login';
import { useFormik } from 'formik';
import { clearTimer } from 'stores';
import * as Yup from 'yup';

import type { Authen } from './type';

import { getInputStatus, hasEmptyValues, isValidEmail, trimObject, trimValue } from 'helpers';
import { useCountdownTime } from 'helpers/count-down-time';
import { confirmedPasswordValidation, requirePasswordValidation } from 'helpers/validate';
import { useString } from 'hooks';
import { apiCall } from 'hooks/service';

import { ERROR_MESSAGE, SUCCESS_MESSAGE } from 'constants/message';
import { PATH } from 'constants/path';

import { defaultLoginRequest } from './constant';
import { loginSchema } from './schema';
import { updateAuthen } from './store';

export const useLoginController = () => {
  const navigation = useNavigate();

  const formik = useFormik({
    initialValues: defaultLoginRequest,
    validationSchema: loginSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const result: Authen = await apiCall({
        url: `/auth/login`,
        data: {
          ...trimObject(values),
          loginType: SystemEnum.WEB,
        },
        method: 'POST',
        isLoading: false,
        showError: true,
      });

      if (result?.token) {
        updateAuthen({
          token: result.token,
          refreshToken: result.refreshToken,
        });

        navigation('/');
      }
    },
  });

  const { values, errors, isSubmitting, isValid, handleSubmit, handleChange } = formik;

  const { account, password } = values;

  const passwordStatus = getInputStatus(
    password,
    SUCCESS_MESSAGE.PASSWORD,
    errors?.password ? ERROR_MESSAGE.PASSWORD : '',
  );

  const emailStatus = getInputStatus(
    account,
    SUCCESS_MESSAGE.EMAIL,
    isValidEmail(account.trim()).isValid ? '' : isValidEmail(account.trim()).message,
  );

  console.log(isValidEmail(account.trim()));

  const disabled =
    hasEmptyValues({ account, password }) ||
    isSubmitting ||
    !isValid ||
    isValidEmail(account.trim()).isValid
      ? false
      : true;

  return {
    account,
    password,
    handleChange,
    handleSubmit,
    passwordStatus,
    disabled,
    emailStatus,
    navigation,
  };
};

export const useForgotPasswordController = () => {
  const navigationTo = useNavigate();

  const formik = useFormik({
    initialValues: {
      account: '',
    },

    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const result: {
        statusCode: number;
      } = await apiCall({
        url: `/auth/forgot-password`,
        data: {
          ...values,
        },
        method: 'POST',
        isLoading: true,
        showError: true,
      });

      if (result.statusCode === HttpStatusCode.NotFound) {
        showToastMessage('error', 'Incorrect email.');

        return;
      }

      navigationTo(PATH.VERIFY_EMAIL, {
        state: {
          account: trimValue(values.account),
        },
      });
    },
  });

  const { values, isSubmitting, isValid, handleSubmit, handleChange } = formik;

  const { account } = values;

  const emailStatus = getInputStatus(
    account,
    SUCCESS_MESSAGE.EMAIL,
    isValidEmail(account.trim()).isValid ? '' : isValidEmail(account.trim()).message,
  );

  const disabled =
    hasEmptyValues({ account }) || isSubmitting || !isValid || isValidEmail(account.trim()).isValid
      ? false
      : true;

  return { account, disabled, navigationTo, handleSubmit, handleChange, emailStatus };
};

export const useCreatePasswordController = () => {
  const navigationTo = useNavigate();
  const location = useLocation();
  const token = useString('');

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tokenValue = searchParams.get('token');

    if (tokenValue) {
      token.setValue(tokenValue);
    }
  }, [location]);

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmedPassword: '',
      token: '',
    },

    validationSchema: Yup.object().shape({
      password: requirePasswordValidation,
      confirmedPassword: confirmedPasswordValidation,
    }),

    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);

      const { password } = values;
      const result: { id: string } = await apiCall({
        url: `/auth/reset-password`,
        data: trimObject({
          password,
          token: token.value,
          confirmedPassword: values.password,
        }),
        method: 'POST',
        isLoading: true,
        showError: true,
      });

      if (result?.id) {
        localStorage.clear();
        showToastMessage('success', 'Saved new password. You can now log in with this password.');
        navigationTo(PATH.LOGIN);
      }
    },
  });

  const { values, errors, isSubmitting, isValid, handleSubmit, handleChange } = formik;

  const { password, confirmedPassword } = values;

  const passwordStatus = getInputStatus(
    password,
    SUCCESS_MESSAGE.PASSWORD,
    errors?.password ? ERROR_MESSAGE.PASSWORD : '',
  );

  const confirmedPasswordStatus = getInputStatus(
    confirmedPassword,
    SUCCESS_MESSAGE.PASSWORD,
    errors?.confirmedPassword
      ? values.confirmedPassword.length >= 6
        ? ERROR_MESSAGE.CONFIRMED_PASSWORD_NOT_MATCHING
        : ERROR_MESSAGE.CONFIRMED_PASSWORD
      : '',
  );

  // const confirmedPasswordStatus = getInputStatus(
  //   confirmedPassword,
  //   SUCCESS_MESSAGE.PASSWORD,
  //   errors?.confirmedPassword ? ERROR_MESSAGE.PASSWORD : '',
  // );

  const disabled =
    hasEmptyValues({ password, confirmedPassword }) ||
    isSubmitting ||
    !isValid ||
    password !== confirmedPassword;

  return {
    password,
    confirmedPassword,
    disabled,
    passwordStatus,
    confirmedPasswordStatus,
    handleSubmit,
    handleChange,
  };
};

export const useVerifyEmailController = () => {
  const navigationTo = useNavigate();
  const location = useLocation();
  const { account } = location.state || {};

  const { isTimerRunning, startTimer, timer } = useCountdownTime('forgot_password');

  useEffect(() => {
    if (!account) {
      navigationTo(PATH.LOGIN);
    }
  }, [account]);

  const handleResend = async () => {
    if (isTimerRunning) {
      return;
    }

    const result: boolean = await apiCall({
      url: `/auth/forgot-password`,
      data: {
        account,
      },
      method: 'POST',
      isLoading: true,
      showError: true,
    });

    if (result) {
      clearTimer('forgot_password');
      startTimer();
    }
  };

  return { isTimerRunning, timer, handleResend };
};
